import { useEffect, useState } from "react";
import { Modal, Button, FieldTextarea } from "@knowtex-ai/components";
import sendServerLogging from "../../utils/sendServerLogging";
import userJSON from "../../utils/userJSON";

const STAR_PATH =
  "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";

export default function FeedbackModal({
  open,
  patient,
  rating,
  setRating,
  setUserFeedback,
  setShowFeedbackModal,
  userFeedback,
}) {
  const [hoveredRating, setHoveredRating] = useState(null);
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [showAdditionalFeedback, setShowAdditionalFeedback] = useState(false);
  const [liveMessage, setLiveMessage] = useState("");

  // The dialog persists between openings so it can animate out, so reopening
  // has to do the resetting that a remount used to handle.
  useEffect(() => {
    if (!open) return;
    setHoveredRating(null);
    setAdditionalFeedback(userFeedback?.additional_feedback || "");
    setShowAdditionalFeedback(Number(rating) > 0);
    // Only re-run on open: the values are the starting point for this session,
    // not something to snap back to while the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (rating > 0) {
      setShowAdditionalFeedback(true);
    }
    if (userFeedback?.additional_feedback) {
      setAdditionalFeedback(userFeedback.additional_feedback);
    }
  }, [rating, userFeedback]);

  useEffect(() => {
    const selectedRating = Number(rating) || 0;
    setLiveMessage(
      selectedRating > 0
        ? `Feedback rating selector ready. ${selectedRating} star${selectedRating > 1 ? "s" : ""} currently selected.`
        : "Feedback rating selector ready. No rating currently selected."
    );
  }, [rating]);

  const feedbackObject = {
    rating: rating,
    additional_feedback: additionalFeedback,
    timestamp: Math.floor(Date.now() / 1000),
  };

  const handleSubmit = () => {
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "SUBMIT_FEEDBACK",
        eventData: {
          mrn: patient.mrn,
          additional_feedback: additionalFeedback,
          rating: rating,
        },
        patientId: patient.patientId,
      },
    });

    setUserFeedback(feedbackObject);
    setShowFeedbackModal(false);
  };

  const handleRatingClick = (star) => {
    setRating(star);
    setLiveMessage(`Feedback rating selected: ${star} star${star > 1 ? "s" : ""}.`);
    setShowAdditionalFeedback(true);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setShowFeedbackModal(false);
      }}
      title="Share your experience"
      subtitle="How is your experience with Knowtex so far?"
      size="small"
    >
      <div className="flex flex-col items-center gap-4">
        {/* Live region for screen readers */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {liveMessage}
        </div>

        {/* Star rating */}
        <p id="feedback-rating-label" className="sr-only">
          Feedback rating
        </p>
        <div className="flex gap-2" role="radiogroup" aria-labelledby="feedback-rating-label">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => handleRatingClick(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              className="p-1 transition-transform duration-150 hover:scale-110"
              aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
              role="radio"
              aria-checked={star === rating}
            >
              <svg
                className="w-9 h-9"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d={STAR_PATH}
                  fill={star <= (hoveredRating || rating) ? "#FFD700" : "none"}
                  stroke={star <= (hoveredRating || rating) ? "#FFD700" : "#9CA3AF"}
                  strokeWidth={star <= (hoveredRating || rating) ? 0 : 1.5}
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ))}
        </div>

        {/* Textarea and submit share a tighter gap than the stack above them */}
        <div className="flex w-full flex-col gap-3">
          {showAdditionalFeedback && (
            <FieldTextarea
              aria-label="Additional feedback"
              value={additionalFeedback}
              onChange={(e) => setAdditionalFeedback(e.target.value)}
              placeholder="Leave feedback here."
              rows={3}
              resize="vertical"
            />
          )}

          <Button variant="primary" onClick={handleSubmit} disabled={rating === 0} fullWidth>
            Submit Feedback
          </Button>
        </div>
      </div>
    </Modal>
  );
}
